import * as React from "react";
import { homeLink, request } from "../../../../../services/cpln";
import { NGProviderBrowser } from "../../../ngProviderBrowser";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { v4 as uuidv4 } from "uuid";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { store } from "../../../../../store";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { CpArrayItemTaint } from "../aws/CpArrayItemTaint";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpInputNumber } from "../../../../gvc/testNewForm/CpInputNumber";
import { CpErrorWarningArray } from "../../../../gvc/testNewForm/CpErrorWarningArray";
import { EllipsisOutlined } from "@ant-design/icons";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { NodePoolDetectedTags } from "../detectedTags";
import { NodePoolCoreButton } from "../nodePoolCoreButton";
import { formatSizeForProviderBrowser } from "../../../ngProviderBrowser";

interface Props {
  index: number;
}
export const DigitalOceanNodePool: React.FC<Props> = ({ index }) => {
  const { id: formId } = useCpFormContext();

  const region = useCpFormValue(`digitalocean.region`);
  const name = useCpFormValue(`digitalocean.nodePools.${index}.name`);
  const dropletSize = useCpFormValue(`digitalocean.nodePools.${index}.dropletSize`);
  const overrideImage = useCpFormValue(`digitalocean.nodePools.${index}.overrideImage`);

  const [browserKey, setBrowserKey] = React.useState("");

  return (
    <>
      <div className="cpln-form-section cpln-ant-tab-content">
        <Card>
          <CardTitle>
            Node Pool{name ? ` | ${name}` : ""}
            <CpErrorWarningArrayIcon names={[`digitalocean.nodePools`]} />
          </CardTitle>
          <CardContent>
            <NodePoolDetectedTags name={`digitalocean.nodePools.${index}.labels`} />
            <CpFormItem>
              <CpLabel name={`digitalocean.nodePools.${index}.name`}>Name</CpLabel>
              <CpInput name={`digitalocean.nodePools.${index}.name`} style={{ maxWidth: 600 }} />
              <CpErrorWarning name={`digitalocean.nodePools.${index}.name`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`digitalocean.nodePools.${index}.dropletSize`}>Droplet Size</CpLabel>
              <Space.Compact>
                <CpInput name={`digitalocean.nodePools.${index}.dropletSize`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("dropletSize")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`digitalocean.nodePools.${index}.dropletSize`} />
            </CpFormItem>
            <CpFormItem>
              <div className="flex gap-2">
                <div className="flex flex-col">
                  <CpLabel name={`digitalocean.nodePools.${index}.minSize`}>Min Size</CpLabel>
                  <CpInputNumber name={`digitalocean.nodePools.${index}.minSize`} />
                </div>
                <div className="flex flex-col">
                  <CpLabel name={`digitalocean.nodePools.${index}.maxSize`}>Max Size</CpLabel>
                  <CpInputNumber name={`digitalocean.nodePools.${index}.maxSize`} />
                </div>
              </div>
              <CpErrorWarningArray
                names={[`digitalocean.nodePools.${index}.minSize`, `digitalocean.nodePools.${index}.maxSize`]}
              />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`digitalocean.nodePools.${index}.overrideImage`}>Override Image</CpLabel>
              <Space.Compact>
                <CpInput name={`digitalocean.nodePools.${index}.overrideImage`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("overrideImage")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`digitalocean.nodePools.${index}.overrideImage`} />
            </CpFormItem>
            <div style={{ maxWidth: 900 }}>
              <CpFormItem>
                <div className="flex items-center gap-4">
                  <CpLabel name={`skip`}>Labels</CpLabel>
                  <NodePoolCoreButton name={`digitalocean.nodePools.${index}.labels`} />
                </div>
                <div className="-mt-2">
                  <CpArrayObject
                    factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                    labels={["Name", "Value"]}
                    name={`digitalocean.nodePools.${index}.labels`}
                  >
                    <CpArrayItemNameValuePair />
                  </CpArrayObject>
                </div>
              </CpFormItem>
            </div>
            <div style={{ maxWidth: 900 }}>
              <CpArrayObject
                factoryFn={() => ({ id: uuidv4(), key: "", value: "", effect: "" })}
                labels={["Key", "Value", "Effect"]}
                label="Taints"
                name={`digitalocean.nodePools.${index}.taints`}
              >
                <CpArrayItemTaint />
              </CpArrayObject>
            </div>
          </CardContent>
        </Card>
      </div>
      {browserKey === "dropletSize" ? (
        <NGProviderBrowser
          title="Browse Digital Ocean Droplets"
          initialValue={dropletSize}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            // TODOMK8S paginate
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "sizes",
                cluster: inputToMk8s(values),
              },
            });
            return data.sizes;
          }}
          sortable
          onOk={(value) => {
            formSetValue(formId, `digitalocean.nodePools.${index}.dropletSize`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          filteredColumns={["Name"]}
          initialSort={["Cost per Month", "asc"]}
          rightAlign={["Cost per Month"]}
          monoColumns={["Cost per Month"]}
          labels={["Name", "Cost per Month", "Availability", "vCPUs", "Memory", "Disk"]}
          distinctFilters={["vCPUs", "Memory", "Disk"]}
          tooltips={[
            {
              label: "Availability",
              content: `Shows if the size is available in selected region (${region})`,
            },
          ]}
          getData={(item: any) => {
            let cost = "N/A";
            let price = 0;
            if (item.price_monthly) {
              price = item.price_monthly;
              cost = item.price_monthly.toLocaleString("en-US", { style: "currency", currency: "USD" });
            }

            return [
              item.slug,
              item.slug,
              [price, cost],
              [item.available, item.available ? "Available" : "Not Available"],
              item.vcpus,
              [item.memory, formatSizeForProviderBrowser(item.memory)],
              [item.disk, formatSizeForProviderBrowser(item.disk)],
            ];
          }}
        />
      ) : null}
      {browserKey === "overrideImage" ? (
        <NGProviderBrowser
          title="Browse Digital Ocean Images"
          initialValue={overrideImage}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "images",
                cluster: inputToMk8s(values),
              },
            });
            return data.images.sort((a: any, b: any) => {
              if (a.slug > b.slug) {
                return 1;
              }
              if (a.slug < b.slug) {
                return -1;
              }
              return 0;
            });
          }}
          onOk={(value) => {
            formSetValue(formId, `digitalocean.nodePools.${index}.overrideImage`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Availability", "Slug"]}
          tooltips={[
            {
              label: "Availability",
              content: `Shows if the image is available in selected region (${region})`,
            },
          ]}
          getData={(item: any) => {
            const available = item.regions.includes(region);

            return [item.slug, item.description, [available, available ? "Available" : "Not Available"], item.slug];
          }}
        />
      ) : null}
    </>
  );
};
